import { Inject, Injectable, PLATFORM_ID, NgZone } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, Subscription, timer, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface SystemMessage {
  id: number;
  title: string;
  message: string;
  starts_at: string;
  ends_at: string;
  severity: 'info' | 'warning' | 'critical';
}

@Injectable({
  providedIn: 'root'
})
export class SystemMessageService {
  private apiUrl = environment.apiUrl;
  private messageSubject = new BehaviorSubject<SystemMessage[]>([]);
  public message$ = this.messageSubject.asObservable();
  private pollingSub?: Subscription;
  private readonly sessionKeyPrefix = 'systemMessageSeen:';
  private readonly pollIntervalMs = 60_000; // regelmaessig pruefen, um neue Meldungen in laufenden Sessions zu liefern
  private readonly isBrowser: boolean;

  constructor(private http: HttpClient, @Inject(PLATFORM_ID) platformId: Object, private ngZone: NgZone) {
    this.isBrowser = isPlatformBrowser(platformId);
    if (this.isBrowser) {
      this.startPolling();
    }
  }

  private startPolling(): void {
    if (!this.isBrowser) {
      return;
    }
    this.pollingSub?.unsubscribe();
    // Timer außerhalb von Angular, damit Hydration/Zone-Stabilität nicht blockiert wird
    this.ngZone.runOutsideAngular(() => {
      this.pollingSub = timer(0, this.pollIntervalMs)
        .pipe(switchMap(() => this.loadActiveMessages()))
        .subscribe((msgs) => {
          // Zurück in Angular-Zone für State-Update/Change Detection
          this.ngZone.run(() => this.messageSubject.next(msgs));
        });
    });
  }

  private loadActiveMessages(): Observable<SystemMessage[]> {
    if (!this.isBrowser) {
      return of([]);
    }
    return this.http.get(`${this.apiUrl}/system-messages/active`, { responseType: 'text' as 'text' }).pipe(
      map((raw) => {
        try {
          const cleaned = (raw || '').replace(/^\uFEFF/, '').trim(); // BOM entfernen, falls vorhanden
          const parsed = JSON.parse(cleaned || '{}');
          return Array.isArray((parsed as any)?.items) ? ((parsed as any).items as SystemMessage[]) : [];
        } catch (e) {
          console.warn('[SystemMessage] JSON-Parse fehlgeschlagen', e);
          return [];
        }
      }),
      map((items) => items.filter((m) => !this.hasSeen(m.id))),
      catchError((err) => {
        console.warn('[SystemMessage] Laden fehlgeschlagen', err);
        return of([]);
      })
    );
  }

  markSeen(id: number): void {
    try {
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem(`${this.sessionKeyPrefix}${id}`, '1');
      }
    } catch {
      // Ignoriere Storage-Fehler
    }
    const remaining = (this.messageSubject.value || []).filter((m) => m.id !== id);
    this.messageSubject.next(remaining);
  }

  refresh(): void {
    if (!this.isBrowser) {
      return;
    }
    this.loadActiveMessages().subscribe((msgs) => {
      this.messageSubject.next(msgs);
    });
  }

  clearSeen(): void {
    try {
      if (typeof sessionStorage === 'undefined') {
        return;
      }
      const keysToRemove: string[] = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith(this.sessionKeyPrefix)) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => sessionStorage.removeItem(key));
    } catch {
      // Ignoriere Storage-Fehler
    }
    this.messageSubject.next([]);
    this.refresh();
  }

  private hasSeen(id: number): boolean {
    try {
      if (typeof sessionStorage === 'undefined') {
        return false;
      }
      return sessionStorage.getItem(`${this.sessionKeyPrefix}${id}`) === '1';
    } catch {
      return false;
    }
  }
}
